<!-- TopPriorityApp.svelte - Prioritization & Focus System -->

<script>
	// Stores and components
	import {
		Dilemma,
		pathNames,
		pathValues,
		Criteria,
		CriteriaCategorization,
		CriteriaPolarity,
	} from "./UserData.js";
	import {
		suggestedItemsMap,
		iconMapping,
		suggestedCriteria,
		CriteriaAndTipsExample,
	} from "./SuggestedCriteria.js";
	import { pathID } from "./AppState.js";
	import { PRIORITY_CRITERIA_CONFIG } from "./PriorityCriteriaConfig.js";
	import AddPathButton from "./AddPathButton.svelte";
	import PriorityCriteriaManager from "./PriorityCriteriaManager.svelte";
	import Slider from "./Slider.svelte";
	import TipsSwitch from "./TipsSwitch.svelte";
	import Tips from "./Tips.svelte";
	import ResultsChart from "./ResultsChart.svelte";
	import RadarChart from "./RadarChart.svelte";
	import PriorityRanking from "./PriorityRanking.svelte";
	import FeedbackSection from "./FeedbackSection.svelte";
	import { maxIndex } from "d3";
	import { push } from "svelte-spa-router";
	import { onMount } from "svelte";
	import analytics from "../utils/analytics.js";
	import decisionAnalytics from "../utils/decisionAnalytics.js";
	import cookieManager from "../utils/cookieManager.js";
	import { getTimestamp, exportToExcel, parseExcelFile } from "../utils/excelUtils.js";
	import { captureResultsAsImage, captureFullPageAsImage } from "../utils/imageCapture.js";
	import { 
		prioritizationTemplates, 
		chartColors as colors, 
		resultsChartColors,
		defaultFocusFactors 
	} from "./prioritizationTemplates.js";

	// Prioritization-specific state
	let showTips = $state(true);
	let showTemplates = $state(false);
	let selectedTemplate = $state("");
	let graphCategories = $state([]);
	let graphPaths = $state([]);
	let userItems = $state([]);
	
	// State for "Go back" functionality - saves state before template selection
	let canGoBack = $state(false);
	let savedState = $state(null);

	// Import state
	let isDraggingOver = $state(false);
	let importFileInput = $state(null);

	// Early adopter waitlist form state
	let waitlistForm = $state({ email: '', phone: '', contactMethod: 'email' });
	let isSubmittingWaitlist = $state(false);
	let waitlistSubmitSuccess = $state(false);
	// Use derived state instead of effects for automatic updates
	let priorityRanking = $derived.by(() => {
		if (Object.keys($pathNames).length === 0 || $Criteria.length === 0)
			return [];

		const pathKeys = Object.keys($pathNames);
		const allPathsHaveValues = pathKeys.every(
			(pathKey) =>
				$pathValues[pathKey] &&
				$pathValues[pathKey].length === $Criteria.length,
		);

		if (!allPathsHaveValues) return [];

		const rankings = [];
		Object.entries($pathNames).forEach(([pathKey, pathName]) => {
			const values = $pathValues[pathKey] || [];
			if (values.length > 0) {
				let totalScore = 0;
				let weightSum = 0;

				values.forEach((value, index) => {
					const polarity =
						$CriteriaPolarity[index] || "Higher is better";
					const category =
						$CriteriaCategorization[index] || "Critical";
					const weight = category === "Critical" ? 2 : 1;

					const adjustedValue =
						polarity === "Lower is better" ? 100 - value : value;
					totalScore += adjustedValue * weight;
					weightSum += weight;
				});

				const averageScore = weightSum > 0 ? totalScore / weightSum : 0;
				rankings.push({
					pathKey,
					name: pathName,
					score: averageScore,
					values: values,
				});
			}
		});

		return rankings.sort((a, b) => b.score - a.score);
	});

	// Helper: Check if criterion name matches effort-related patterns
	const isEffortRelated = (name) => {
		const lowerName = name.toLowerCase();
		// Exact matches for known criteria
		if (lowerName === "effort required" || lowerName === "cost") return true;
		// Keyword matches
		const effortKeywords = ["effort", "cost", "time", "resource", "difficult"];
		return effortKeywords.some((kw) => lowerName.includes(kw));
	};

	// Helper: Check if criterion name matches impact-related patterns
	const isImpactRelated = (name) => {
		const lowerName = name.toLowerCase();
		// Exact matches for known criteria
		const exactMatches = [
			"expected impact",
			"expected return on investment",
			"expected return on investment (roi)",
			"estimated return on investment",
			"strategic alignment",
			"risk reduction",
			"momentum",
		];
		if (exactMatches.some((m) => lowerName === m)) return true;
		// Keyword matches
		const impactKeywords = ["impact", "roi", "return", "value", "benefit", "momentum"];
		return impactKeywords.some((kw) => lowerName.includes(kw));
	};

	// Helper: Get criterion name (handles both string and object formats)
	const getCriterionName = (criterion) =>
		typeof criterion === "object" ? criterion.name : criterion;

	// Helper: Calculate average score for matching criteria with polarity adjustment
	const calculateAverageForCriteria = (item, matchFn) => {
		const criteriaList = $Criteria;
		const polarities = $CriteriaPolarity;
		let sum = 0;
		let count = 0;

		criteriaList.forEach((criterion, index) => {
			const name = getCriterionName(criterion);
			if (matchFn(name) && item.values[index] !== undefined) {
				const rawValue = item.values[index];
				const polarity = polarities[index] || "Higher is better";
				// Normalize: for "Lower is better" criteria, invert so high adjusted = good
				const adjustedValue = polarity === "Lower is better" ? 100 - rawValue : rawValue;
				sum += adjustedValue;
				count++;
			}
		});

		return count > 0 ? sum / count : null; // null = no matching criteria found
	};

	let focusMatrix = $derived.by(() => {
		if (priorityRanking.length === 0)
			return {
				highImpactLowEffort: [],
				highImpactHighEffort: [],
				lowImpactLowEffort: [],
				lowImpactHighEffort: [],
			};

		const matrix = {
			highImpactLowEffort: [], // Quick Wins
			highImpactHighEffort: [], // Major Projects
			lowImpactLowEffort: [], // Fill-in Tasks
			lowImpactHighEffort: [], // Time Wasters
		};

		const EFFORT_THRESHOLD = 40; // For top 3: adjusted effort >= 40 = low actual effort = Quick Win
		const IMPACT_THRESHOLD = 60; // For 4+: adjusted impact >= 60 = Fill-in Task

		priorityRanking.forEach((item, rankIndex) => {
			if (rankIndex < 3) {
				// Top 3: Quick Wins vs Major Projects based on effort
				// After polarity adjustment: HIGH adjusted = LOW actual effort (easier)
				const avgEffort = calculateAverageForCriteria(item, isEffortRelated);
				if (avgEffort === null) {
					// Fallback: no effort criteria found, default to Quick Wins
					matrix.highImpactLowEffort.push(item);
				} else if (avgEffort >= EFFORT_THRESHOLD) {
					// High adjusted value = LOW actual effort = easier = Quick Win
					matrix.highImpactLowEffort.push(item);
				} else {
					// Low adjusted value = HIGH actual effort = harder = Major Project
					matrix.highImpactHighEffort.push(item);
				}
			} else {
				// 4th and beyond: Fill-in Tasks vs Time Wasters based on impact
				const avgImpact = calculateAverageForCriteria(item, isImpactRelated);
				if (avgImpact === null) {
					// Fallback: no impact criteria found, default to Fill-in Tasks
					matrix.lowImpactLowEffort.push(item);
				} else if (avgImpact >= IMPACT_THRESHOLD) {
					// Still has decent impact = Fill-in Task
					matrix.lowImpactLowEffort.push(item);
				} else {
					// Low impact = Time Waster
					matrix.lowImpactHighEffort.push(item);
				}
			}
		});

		return matrix;
	});

	// Derived data for ResultsChart using priority ranking scores
	let resultsChartData = $derived.by(() => {
		if (priorityRanking.length === 0) return [];
		// Normalize scores to 0-1 range for the chart
		return priorityRanking.map(item => item.score / 100);
	});

	let resultsChartLabels = $derived.by(() => {
		return priorityRanking.map(item => item.name);
	});

	let resultsChartColorsArray = $derived.by(() => {
		return priorityRanking.map((_, index) => 
			resultsChartColors[Math.min(index, resultsChartColors.length - 1)]
		);
	});

	// Line weights for RadarChart: thicker for top, thinner for low priority (gray items)
	let radarLineWeights = $derived.by(() => {
		return priorityRanking.map((_, index) => 
			index === 0 ? 4 : index < 3 ? 2 : 1
		);
	});

	// Initialize with focus factors
	// Store decision ID for feedback tracking
	let currentDecisionId = $state(null);

	onMount(() => {
		// Initialize analytics tracking for TopPriorityApp
		analytics.trackPageView("priorizator_app", {
			timestamp: Date.now(),
			userAgent: navigator.userAgent,
			referrer: document.referrer,
		});

		// Start prioritization analytics tracking
		decisionAnalytics.startDecision();
		currentDecisionId = decisionAnalytics.currentDecision;

		// Initialize default focus factors if criteria is empty
		if ($Criteria.length === 0) {
			Criteria.set(defaultFocusFactors);
			CriteriaCategorization.set(
				defaultFocusFactors.map((f) => f.category),
			);
			CriteriaPolarity.set(defaultFocusFactors.map((f) => f.polarity));
		}
	});

	// Template selection handler
	function selectTemplate(templateKey) {
		// Save current state before applying template
		savedState = {
			dilemma: $Dilemma,
			pathNames: { ...$pathNames },
			pathValues: JSON.parse(JSON.stringify($pathValues)), // Deep copy
			userItems: [...userItems],
			pathID: $pathID, // Save the current pathID counter
		};
		canGoBack = true;

		selectedTemplate = templateKey;
		const template = prioritizationTemplates[templateKey];

		// Build userItems from template criteria
		userItems = template.criteria.map((criteriaName, index) => {
			const criteriaConfig = PRIORITY_CRITERIA_CONFIG.find(c => c.name === criteriaName);
			const category = index < 2 ? "Critical" : "Important"; // First 2 are Critical
			
			return {
				name: criteriaName,
				category: category,
				polarity: criteriaConfig?.polarity || "Higher is better",
				emoji: criteriaConfig?.emoji || "⭐",
				description: criteriaConfig?.description || ""
			};
		});

		// Set dilemma - use full dilemma if available, otherwise fall back to example
		$Dilemma = template.dilemma || template.example;

		// If template has projects and scores, populate them for a full example
		if (template.projects && template.scores) {
			// Build pathNames object: {"Path 0": "Project Name", "Path 1": "Project Name", ...}
			const newPathNames = {};
			template.projects.forEach((projectName, index) => {
				newPathNames[`Path ${index}`] = projectName;
			});
			pathNames.set(newPathNames);

			// Build pathValues object: {"Path 0": [score1, score2, ...], "Path 1": [score1, score2, ...], ...}
			const newPathValues = {};
			template.projects.forEach((projectName, index) => {
				newPathValues[`Path ${index}`] = template.scores[projectName] || 
					new Array(template.criteria.length).fill(50); // Default to 50 if no scores
			});
			pathValues.set(newPathValues);

			// CRITICAL: Update pathID to match the number of template projects
			// This prevents overwriting existing projects when user adds new ones
			pathID.set(template.projects.length);
		}

		// CRITICAL: Sync userItems to stores so Focus Matrix uses correct polarities
		Criteria.set([...userItems]);
		CriteriaCategorization.set(userItems.map((i) => i.category));
		CriteriaPolarity.set(userItems.map((i) => i.polarity));

		showTemplates = false;
		showTips = false; // Deactivate guide when using a template

		// Track template selection
		analytics.track("template_selected", {
			template: templateKey,
			templateName: template.name,
			hasFullExample: !!(template.projects && template.scores),
		});
	}

	// Go back to previous state before template was selected
	function goBack() {
		if (!savedState) return;

		// Restore all saved state
		$Dilemma = savedState.dilemma;
		pathNames.set(savedState.pathNames);
		pathValues.set(savedState.pathValues);
		userItems = savedState.userItems;
		pathID.set(savedState.pathID); // Restore pathID counter

		// Clear the saved state and hide the button
		savedState = null;
		canGoBack = false;
		selectedTemplate = "";

		// Track the action
		analytics.track("template_go_back", {});
	}

	// Rest of the original FocalPath functionality adapted for prioritization
	let numPaths = $derived(Object.keys($pathNames).length);
	let numCriteria = $derived($Criteria.length);

	// Keep the original effect for pathValues management
	$effect.pre(() => {
		const pathKeys = Object.keys($pathNames);
		const pathValueKeys = Object.keys($pathValues);
		const currentCriteriaLength = $Criteria.length;

		const keysToRemove = pathValueKeys.filter(
			(key) => !pathKeys.includes(key),
		);
		if (keysToRemove.length > 0) {
			pathValues.update((currentValues) => {
				for (const keyToRemove of keysToRemove) {
					delete currentValues[keyToRemove];
				}
				return currentValues;
			});
		}

		const needsCriteriaCleanup =
			currentCriteriaLength > 0 &&
			pathKeys.some(
				(pathKey) =>
					$pathValues[pathKey] &&
					$pathValues[pathKey].length > currentCriteriaLength,
			);

		if (needsCriteriaCleanup) {
			pathValues.update((currentValues) => {
				const updatedValues = { ...currentValues };
				pathKeys.forEach((pathKey) => {
					if (
						updatedValues[pathKey] &&
						updatedValues[pathKey].length > currentCriteriaLength
					) {
						updatedValues[pathKey] = updatedValues[pathKey].slice(
							0,
							currentCriteriaLength,
						);
					}
				});
				return updatedValues;
			});

			if (backendResults) {
				backendResults = null;
			}
		}

		// Initialize pathValues[pathKey] with an array of default values (e.g., 50)
		pathKeys.forEach((pathKey) => {
			if (!$pathValues[pathKey]) {
				// Always initialize missing paths
				$pathValues[pathKey] = new Array(currentCriteriaLength).fill(
					50,
				);
			} else if ($pathValues[pathKey].length < currentCriteriaLength) {
				// Extend arrays for new criteria
				const currentLength = $pathValues[pathKey].length;
				const newValues = new Array(
					currentCriteriaLength - currentLength,
				).fill(50);
				pathValues.update((currentValues) => ({
					...currentValues,
					[pathKey]: [...currentValues[pathKey], ...newValues],
				}));
			}
		});
	});

	// Reactive statement to transform data for graphs
	$effect(() => {
		graphCategories = $Criteria.map((criterion, i) => {
			const criterionObj =
				typeof criterion === "object" ? criterion : { name: criterion };
			return {
				...criterionObj,
				type: $CriteriaCategorization[i] || "Critical",
				polarity: $CriteriaPolarity[i] || "Higher is better",
			};
		});

		// Build graphPaths sorted by priority ranking order (matches ResultsChart)
		// Use the same colors as ResultsChart for visual consistency
		graphPaths = priorityRanking
			.filter(
				(item) =>
					$pathValues[item.pathKey] &&
					$pathValues[item.pathKey].length === $Criteria.length,
			)
			.map((item, index) => {
				return {
					name: item.name,
					values: $pathValues[item.pathKey].map(
						(value, criterionIndex) => {
							const polarity =
								$CriteriaPolarity[criterionIndex] ||
								"Higher is better";
							return polarity === "Lower is better"
								? 100 - value
								: value;
						},
					),
					color: resultsChartColors[Math.min(index, resultsChartColors.length - 1)],
				};
			});
	});

	// Backend processing (adapted for prioritization)
	let backendResults = $state(null);
	let isProcessing = $state(false);

	const handleSubmit = async (e) => {
		e?.preventDefault?.();
		isProcessing = true;

		if ($CriteriaPolarity.length !== $Criteria.length) {
			console.warn(
				`Polarity array length (${$CriteriaPolarity.length}) doesn't match criteria length (${$Criteria.length}). Using default polarity for missing items.`,
			);
		}

		const processedPathValues = {};
		for (const [pathKey, values] of Object.entries($pathValues)) {
			processedPathValues[pathKey] = values.map((value, index) => {
				const polarity = $CriteriaPolarity[index] || "Higher is better";
				return polarity === "Lower is better" ? 100 - value : value;
			});
		}

		const criteriaNames = $Criteria.map((criterion) =>
			typeof criterion === "object" ? criterion.name : criterion,
		);

		try {
			const response = await fetch("/process-data", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					dilema: $Dilemma,
					dilemma: $Dilemma,
					pathNames: $pathNames,
					pathValues: processedPathValues,
					criteria: criteriaNames,
					criteriaCategorization: $CriteriaCategorization,
					// Analytics data
					sessionId: cookieManager.sessionId,
					userId: cookieManager.userId,
					decisionId: decisionAnalytics.currentDecision,
					sessionDuration: cookieManager.getSessionDuration(),
					// Prioritization-specific metadata
					appType: "prioritization",
					templateUsed: selectedTemplate,
				}),
			});

			if (response.ok) {
				const results = await response.json();

				const randomDelay = Math.random() * 1000 + 1500;
				setTimeout(() => {
					backendResults = results;
					isProcessing = false;

					// Track prioritization completion
					const completionData = {
						pathNames: $pathNames,
						criteria: criteriaNames,
						criteriaCategorization: $CriteriaCategorization,
						results: results,
						usedTips: showTips,
						templateUsed: selectedTemplate,
						topPriorities: priorityRanking
							.slice(0, 3)
							.map((p) => p.name),
					};
					decisionAnalytics.trackDecisionCompleted(
						results,
						completionData,
					);

					setTimeout(() => {
						const resultsSection =
							document.getElementById("results-section");
						if (resultsSection) {
							resultsSection.scrollIntoView({
								behavior: "smooth",
								block: "start",
							});
						}
					}, 100);
				}, randomDelay);
			} else {
				isProcessing = false;
				console.error("Error:", response.status);
			}
		} catch (error) {
			isProcessing = false;
			console.error("Network error:", error);
		}
	};

	function goToHome() {
		// On TopPriority subdomain, go to root; on main domain, go to /TopPriority
		const isTopPrioritySubdomain = window.location.hostname.toLowerCase().startsWith('toppriority.');
		push(isTopPrioritySubdomain ? "/" : "/TopPriority");
	}

	// Polarity toggle function
	const toggleCriterionPolarity = (index) => {
		// Update the CriteriaPolarity store
		CriteriaPolarity.update((polarities) => {
			const newPolarities = [...polarities];
			while (newPolarities.length <= index) {
				newPolarities.push("Higher is better");
			}
			newPolarities[index] =
				newPolarities[index] === "Higher is better"
					? "Lower is better"
					: "Higher is better";
			
			// Also update the userItems array to keep them in sync
			if (userItems[index]) {
				userItems[index] = {
					...userItems[index],
					polarity: newPolarities[index]
				};
			}
			
			return newPolarities;
		});
	};

	// Early adopter waitlist submission handler
	async function handleWaitlistSubmit(event) {
		event.preventDefault();
		
		if (!waitlistForm.email.trim()) {
			alert('Please enter your email address');
			return;
		}

		isSubmittingWaitlist = true;
		const isDevelopment = window.location.port === '5174' || window.location.hostname === 'localhost';
		
		try {
			const waitlistData = {
				email: waitlistForm.email.trim(),
				phone: waitlistForm.phone.trim(),
				contactMethod: waitlistForm.contactMethod,
				userId: cookieManager.userId,
				sessionId: cookieManager.sessionId,
				source: 'early_adopter_insights'
			};

			const apiUrl = isDevelopment 
				? 'http://localhost:5000/api/waitlist' 
				: '/api/waitlist';

			const controller = new AbortController();
			const timeoutDuration = isDevelopment ? 5000 : 15000;
			const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);
				
			const response = await fetch(apiUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(waitlistData),
				signal: controller.signal
			});
			
			clearTimeout(timeoutId);

			let result;
			try {
				result = await response.json();
			} catch (parseError) {
				throw new Error(`Server error: ${response.status} ${response.statusText}`);
			}

			if (response.ok && result.status === 'success') {
				waitlistSubmitSuccess = true;
			} else {
				const errorMessage = result.message || 'Failed to join waitlist. Please try again.';
				alert(errorMessage);
			}
			
		} catch (error) {
			if (isDevelopment) {
				if (error.name === 'AbortError' || 
					error.message.includes('Failed to fetch') || 
					error.message.includes('The operation was aborted') ||
					error instanceof DOMException ||
					error.message.includes('Server error: 404')) {
					console.log('Development mode: Simulating waitlist signup');
					waitlistSubmitSuccess = true;
				} else {
					console.error('Unexpected development error:', error);
					alert('Development error: ' + error.message);
				}
			} else {
				console.error('Waitlist submission error:', error);
				alert('Something went wrong. Please check your connection and try again.');
			}
		} finally {
			isSubmittingWaitlist = false;
		}
	}

	// Flags for user feedback
	let isExtraHelpNeeded = $state(false);
	let isNarrowFraming = $derived(numPaths < 3);
	let isDeepAnalysis = $derived(numCriteria + numPaths >= 7);

	// Save menu state
	let showSaveMenu = $state(false);

	// Toggle save menu
	function toggleSaveMenu() {
		showSaveMenu = !showSaveMenu;
	}

	// Close save menu when clicking outside
	function handleClickOutside(event) {
		const saveContainer = document.querySelector('.save-button-container');
		if (saveContainer && !saveContainer.contains(event.target)) {
			showSaveMenu = false;
		}
	}

	// Helper: Get focus matrix quadrant for a project
	function getFocusMatrixQuadrant(projectName) {
		if (focusMatrix.highImpactLowEffort.some(item => item.name === projectName)) return "Quick Wins";
		if (focusMatrix.highImpactHighEffort.some(item => item.name === projectName)) return "Major Projects";
		if (focusMatrix.lowImpactLowEffort.some(item => item.name === projectName)) return "Fill-in Tasks";
		if (focusMatrix.lowImpactHighEffort.some(item => item.name === projectName)) return "Time Wasters";
		return "";
	}

	// Import from Excel file (uses excelUtils)
	const importFromExcel = async (file) => {
		try {
			// Parse the file using utility function
			const data = await parseExcelFile(file);

			// Save current state before import (for Go Back functionality)
			savedState = {
				dilemma: $Dilemma,
				pathNames: { ...$pathNames },
				pathValues: JSON.parse(JSON.stringify($pathValues)),
				userItems: [...userItems],
				pathID: $pathID,
			};
			canGoBack = true;

			// Build userItems from imported criteria
			const newUserItems = data.criteria.map(criterion => {
				const criteriaConfig = PRIORITY_CRITERIA_CONFIG.find(c => c.name === criterion.name);
				return {
					name: criterion.name,
					category: criterion.category,
					polarity: criterion.polarity,
					emoji: criteriaConfig?.emoji || '⭐',
					description: criteriaConfig?.description || ''
				};
			});
			userItems = newUserItems;

			// Build pathNames and pathValues from parsed projects
			const newPathNames = {};
			const newPathValues = {};

			data.projects.forEach((project, index) => {
				const pathKey = `Path ${index}`;
				newPathNames[pathKey] = project.name;
				newPathValues[pathKey] = project.scores;
			});

			pathNames.set(newPathNames);
			pathValues.set(newPathValues);
			pathID.set(data.projects.length);

			// Restore dilemma if available
			if (data.dilemma) {
				$Dilemma = data.dilemma;
			}

			// Sync to stores
			Criteria.set([...newUserItems]);
			CriteriaCategorization.set(newUserItems.map(i => i.category));
			CriteriaPolarity.set(newUserItems.map(i => i.polarity));

			// Hide templates and show form (same as template selection)
			selectedTemplate = '';
			showTemplates = false;
			showTips = false;

			// Track import
			analytics.track('import_from_excel', {
				criteriaCount: data.criteria.length,
				projectCount: data.projects.length,
				hasMetadata: data.hasMetadata
			});

		} catch (error) {
			console.error('Error importing Excel file:', error);
			alert(error.message || 'Could not import the file. Please ensure it is a valid Top Priority export file.');
		}
	};

	// Handle file input change
	function handleFileSelect(event) {
		const file = event.target.files?.[0];
		if (file) {
			importFromExcel(file);
		}
		// Reset input so same file can be selected again
		event.target.value = '';
	}

	// Handle drag and drop
	function handleDragOver(event) {
		event.preventDefault();
		isDraggingOver = true;
	}

	function handleDragLeave(event) {
		event.preventDefault();
		isDraggingOver = false;
	}

	function handleDrop(event) {
		event.preventDefault();
		isDraggingOver = false;
		
		const file = event.dataTransfer?.files?.[0];
		if (file) {
			importFromExcel(file);
		}
	}

	// Save results section only as image (uses imageCapture utils)
	const saveResultsAsImage = async () => {
		showSaveMenu = false;
		try {
			await captureResultsAsImage();
		} catch (error) {
			console.error("Error capturing results screenshot:", error);
			alert("Could not capture results. Please try 'Save everything as image' instead.");
		}
	};

	// Save everything as Excel (uses excelUtils)
	const saveAsExcel = async () => {
		showSaveMenu = false;
		
		try {
			// Build criteria array with proper structure
			const criteriaData = $Criteria.map((c, i) => ({
				name: typeof c === 'object' ? c.name : c,
				polarity: $CriteriaPolarity[i] || 'Higher is better',
				category: $CriteriaCategorization[i] || 'Important'
			}));

			await exportToExcel({
				dilemma: $Dilemma,
				templateName: selectedTemplate ? prioritizationTemplates[selectedTemplate]?.name || selectedTemplate : 'Custom',
				numProjects: numPaths,
				numCriteria: numCriteria,
				criteria: criteriaData,
				priorityRanking: priorityRanking,
				getFocusMatrixQuadrant: getFocusMatrixQuadrant
			});
			
		} catch (error) {
			console.error('Error exporting Excel:', error);
			alert('Could not export Excel file. Please try again.');
		}
	};

	// Save everything as image (uses imageCapture utils)
	const saveEverythingAsImage = async () => {
		showSaveMenu = false;
		try {
			await captureFullPageAsImage();
		} catch (error) {
			console.error("Error capturing screenshot:", error);
			window.print(); // Fallback to print
		}
	};

	// Mount/unmount click outside listener
	$effect(() => {
		if (showSaveMenu) {
			document.addEventListener('click', handleClickOutside);
			return () => document.removeEventListener('click', handleClickOutside);
		}
	});
</script>

<!-- Header Section -->
<section class="hero is-bold prioritization-hero">
	<div class="hero-body">
		<div
			class="container has-text-centered"
			role="button"
			tabindex="0"
			onclick={goToHome}
			onkeydown={goToHome}
		>
			<div class="header-logo-container">
				<img 
					src="/Icons/TopPriority-Logo-Circles.svg" 
					alt="Top Priority Logo" 
					class="header-logo"
				/>
			</div>
			<h1 class="title prioritization-title">Top Priority</h1>
			<h2 class="subtitle prioritization-subtitle">
				<!-- Find your #1. Ignore the rest. -->
				Find your #1. Move the needdle.
			</h2>
		</div>
	</div>
</section>

<div class="appContent">
	<!-- Templates Section -->
	{#if showTemplates}
		<div class="templates-container">
			<h3>Choose a prioritization template:</h3>
			<div class="templates-grid">
				{#each Object.entries(prioritizationTemplates) as [key, template]}
					<button
						class="template-card"
						onclick={() => selectTemplate(key)}
						type="button"
					>
						<h4>{template.name}</h4>
						<p>{template.description}</p>
						<div class="template-criteria">
							{#each template.criteria as criterion}
								<span class="criterion-tag">{criterion}</span>
							{/each}
						</div>
					</button>
				{/each}

				<!-- Import from Excel Card -->
				<button
					class="template-card import-card"
					class:dragging={isDraggingOver}
					type="button"
					onclick={() => importFileInput?.click()}
					ondragover={handleDragOver}
					ondragleave={handleDragLeave}
					ondrop={handleDrop}
				>
					<div class="import-icon">📥</div>
					<h4>Import from Excel</h4>
					<p>Continue a previous analysis</p>
					<div class="import-hint">
						{#if isDraggingOver}
							<span class="drop-hint">Drop file here</span>
						{:else}
							<span>Click or drag & drop .xlsx file</span>
						{/if}
					</div>
					<input
						type="file"
						accept=".xlsx,.xls"
						bind:this={importFileInput}
						onchange={handleFileSelect}
						style="display: none;"
					/>
				</button>
			</div>
			<button
				class="skip-templates"
				onclick={() => (showTemplates = false)}
			>
				Skip templates - start from scratch
			</button>
		</div>
	{:else}
		<!-- Main Prioritization Interface -->
		<div class="prioritization-input">
			<div class="textarea-container">
				<textarea
					placeholder="📝 What is competing for your attention right now? List everything on your plate - work projects, side hustles, personal goals... or start from a template:💡"
					title="Brain dump everything without judgment - we'll help you prioritize"
					bind:value={$Dilemma}
				></textarea>
			</div>

			<div class="tips-container">
				<TipsSwitch
					bind:active={showTips}
					icon="🧭"
					tooltip="Guide me"
					backgroundColor="#d6fffd"
					ontoggle={(val) => console.log("showTips toggled to", val)}
					id="tips-toggle"
				/>

				<TipsSwitch
					icon="💡"
					tooltip="Use a template"
					bind:active={showTemplates}
					backgroundColor="#e8f4fd"
					ontoggle={(val) => (showTemplates = val)}
					id="template-toggle"
				/>

				{#if canGoBack}
					<TipsSwitch
						icon="↩️"
						tooltip="Go back"
						active={false}
						backgroundColor="#fff3e0"
						ontoggle={() => goBack()}
						id="go-back-toggle"
					/>
				{/if}
			</div>

			{#if showTips}
				<Tips>
					{#snippet children()} <!-- is a false positive - it's the valid Svelte 5 {#snippet children()} syntax which older linters don't recognize. This is not an actual bug. -->
						<p
							style="font-size: medium; text-align:center; margin-bottom:1em;"
						>
							🧭 Guide 🧭
						</p>
						<p>
							Brain dump <b>everything</b> competing for your
							<b>attention</b>
							without judgment. Include both urgent tasks and important
							long-term goals.
						</p>
						<br />
						<p>
							We'll help you separate what truly <b
								>moves the needle</b
							> from what just feels urgent.
						</p>
						<br />
						<p>
							When ready, click <b>Add</b> to start building
							your list.
						</p>
					{/snippet}
				</Tips>
			{/if}
		</div>

		{#if numPaths > 0}
			<p class="subtle-text" style="padding:2em;">
				<!-- What projects really need your focus? -->
				 Identify individual tasks or projects
			</p>
		{/if}

		<AddPathButton
			bind:pathNames={$pathNames}
			buttonText="Add"
			itemIcon="🚀"
			itemPlaceholder="Task / Project"
		/>
		<div
			class="AddPathButton-scrolling-anchor"
			style="margin-top:1em"
		></div>

		{#if numPaths > 1}
			<div class="gap" style="margin-bottom: 100px;"></div>

			{#if showTips && numPaths > 0}
				<Tips>
					{#snippet children()}
						Now think about what is <b>important</b> to you <br><br>
						Remember, if everything is important, then nothing really is. Pick the factors that matter most to you right now.
					{/snippet}
				</Tips>
			{/if}

			<PriorityCriteriaManager bind:userItems />



			{#if showTips && numCriteria > 0}
				<Tips>
					{#snippet children()}
						Click the "Critical/Important" tags to adjust how much
						weight each factor gets in your prioritization.
					{/snippet}
				</Tips>
			{/if}

			<div class="gap" style="margin-bottom: 50px;"></div>

			{#if numCriteria > 0}
				<p style="text-align: center; padding: 1.5em; margin-top:-1em;">
					Now <b>evaluate each project</b> on one factor at a time.
					Think about them <b>relative to each other</b>.
				</p>

				{#if showTips}
					<Tips>
						{#snippet children()}
							<p>
								Focus on the <b>relationship</b> between projects
								rather than absolute scores. If Project A has twice
								the impact of Project B, make sure the sliders reflect
								that.
							</p>

						{/snippet}
					</Tips>
				{/if}

				{#each $Criteria as c, index}
					<div id="assessment-section">
						<div class="has-text-centered">
							<div
								class="criteria-container"
								style="width: 100%; max-width: 600px; margin-top: 1em;"
							>
								{#if c.emoji}
									<span class="criteria-emoji" style="font-size: 4em; display: block; margin-bottom: 0.5em;">
										{c.emoji}
									</span>
								{:else}
									<img
										src={c.icon ||
											iconMapping[c.name || c] ||
											iconMapping["default"]}
										alt={c.name || c}
										class="criteria-icon"
										style="height: 4em; width: auto;"
									/>
								{/if}

								<div style="padding: 1em">
									{#if c.description}
										<p
											style="font-size: 1em; color: #333; margin: 0; line-height: 1.4; text-align: center;"
										>
											{c.description}
										</p>
									{:else}
										<p
											style="font-size: 1em; color: #333; margin: 0; line-height: 1.4; text-align: center;"
										>
											{c.name || c}
										</p>
									{/if}
								</div>

								<div style="width: 100%;">
									{#each Object.entries($pathNames) as [pathKey, Name]}
										<Slider
											bind:value={
												$pathValues[pathKey][index]
											}
										>
											{#snippet children()}
												<li title={Name}>{Name}</li>
											{/snippet}
										</Slider>
									{/each}
								</div>

								{#if !(suggestedItemsMap[c.name || c] === "Higher is better" && (c.polarity || $CriteriaPolarity[index] || "Higher is better") === "Higher is better")}
									<div
										style="text-align: center; margin-top: 1em; margin-bottom: 1em;"
									>
										<div
											class={($CriteriaPolarity[index] ||
												"Higher is better") ===
											"Higher is better"
												? "tag is-primary is-light"
												: "tag is-warning is-light"}
											role="button"
											tabindex="0"
											style="cursor: pointer; font-size: 0.9em;"
											onclick={() =>
												toggleCriterionPolarity(index)}
											onkeydown={(e) =>
												e.key === "Enter" &&
												toggleCriterionPolarity(index)}
										>
											{$CriteriaPolarity[index] ||
												"Higher is better"}
										</div>
									</div>
								{/if}
							</div>
						</div>
					</div>
				{/each}

				{#if showTips}
					<Tips>
						{#snippet children()}
							Take your time to get the relationships right. You
							can always go back to adjust.
							<br /><br />
							When you're satisfied with your assessment, hit
							<b>Get My Priorities</b> to see your focus hierarchy.
						{/snippet}
					</Tips>
				{/if}

				<div style="padding: 50px">
					{#if isProcessing}
						<div class="button-loading-container">
							<div class="spinning-icon-container">
								<img
									src="/Icons/TopPriority-Logo-Circles.svg"
									alt="Processing"
									class="spinning-priority-icon"
								/>
							</div>
							<div class="weighing-text">
								Calculating priorities...
							</div>
						</div>
					{:else}
						<form class="form-container" onsubmit={handleSubmit}>
							<button type="submit" class="priority-button"
								>Get My Priorities</button
							>
						</form>
					{/if}
				</div>
			{/if}
		{/if}
	{/if}

	<!-- Fade Overlay -->
	{#if isProcessing}
		<div class="fade-overlay"></div>
	{/if}

	{#if backendResults}
		<div id="results-section">
			<h1 style="margin-top: 4em; margin-bottom: 2em;">
				Your Focus Hierarchy
			</h1>

			<!-- Priority Results Chart -->
			<ResultsChart
				data={resultsChartData}
				labels={resultsChartLabels}
				colors={resultsChartColorsArray}
			/>

			<!-- Detailed Analysis with Radar Chart -->
			{#if graphCategories.length > 2}
				<h2 style="margin: 2em;">Detailed Analysis</h2>
				<RadarChart
					categories={graphCategories}
					paths={graphPaths}
					{iconMapping}
					lineWeights={radarLineWeights}
				/>
			{/if}

			<!-- Priority Ranking Display -->
			<PriorityRanking 
				priorities={priorityRanking}
				title="Top Priority this week:"
				lowerPriorityTitle="Low Priority = Distractions until top 3 achieved:"
				scoreLabel="Focus Score"
			/>

			<!-- Focus Matrix -->
			{#if Object.values(focusMatrix).some((arr) => arr.length > 0)}
				<div class="focus-matrix">
					<h2>Your Focus Matrix</h2>
					<div class="matrix-grid">
						<div class="matrix-quadrant high-impact-low-effort">
							<h4>🎯 Quick Wins</h4>
							<p>Do now</p>
							{#each focusMatrix.highImpactLowEffort as item}
								<div class="matrix-item">{item.name}</div>
							{/each}
						</div>
						<div class="matrix-quadrant high-impact-high-effort">
							<h4>🚀 Major Projects</h4>
							<p>After quick wins</p>
							{#each focusMatrix.highImpactHighEffort as item}
								<div class="matrix-item">{item.name}</div>
							{/each}
						</div>
						<div class="matrix-quadrant low-impact-low-effort">
							<h4>📋 Fill-in Tasks</h4>
							<p>Consider delegating</p>
							{#each focusMatrix.lowImpactLowEffort as item}
								<div class="matrix-item">{item.name}</div>
							{/each}
						</div>
						<div class="matrix-quadrant low-impact-high-effort">
							<h4>🗑️ Time Wasters</h4>
							<p>Consider discarding</p>
							{#each focusMatrix.lowImpactHighEffort as item}
								<div class="matrix-item">{item.name}</div>
							{/each}
						</div>
					</div>
				</div>
			{/if}

			<div class="block" style="display: flex; justify-content: center; margin-bottom: 3rem;">
				<div style="text-align: left;">
					<p style="margin-bottom: 0.5rem;">
						<span style="font-size: 1.2rem;">✅ <strong>Prioritize</strong></span>
					</p>
					<p style="margin-bottom: 0.5rem;">
						<span style="font-size: 1.2rem;"><span style="font-size: 1.6rem; padding: 0.1rem;">☐</span> <strong>Execute</strong></span>
					</p>
				</div>
			</div>
		</div>
			<!-- Save Button with Dropdown Menu -->
			<div style="display: flex; justify-content: flex-end; width: 100%; max-width: 600px; margin-top: 1em; padding-right: 1em;">
				<div class="save-button-container">
					<button 
						onclick={toggleSaveMenu}
						class="save-button"
						title="Save Analysis"
						aria-label="Save Analysis"
						aria-expanded={showSaveMenu}
					>
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
							<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
							<polyline points="17 21 17 13 7 13 7 21"></polyline>
							<polyline points="7 3v5h8"></polyline>
						</svg>
						<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="chevron-icon" class:rotated={showSaveMenu}>
							<polyline points="6 9 12 15 18 9"></polyline>
						</svg>
					</button>

					{#if showSaveMenu}
						<div class="save-menu">
							<button class="save-menu-item" onclick={saveResultsAsImage}>
								<span class="menu-icon">📊</span>
								Save results as image
							</button>
							<button class="save-menu-item" onclick={saveEverythingAsImage}>
								<span class="menu-icon">📸</span>
								Save everything as image
							</button>
							<button class="save-menu-item" onclick={saveAsExcel}>
								<span class="menu-icon">🗂️</span>
								Save as Excel
							</button>
						</div>
					{/if}
				</div>
			</div>

			<!-- User Feedback Section -->
			<FeedbackSection appType="top_priority" decisionId={currentDecisionId} />
		

		<footer class="footer">
			<div class="container" style="font-size: small;">
				<div class="content has-text-right">
					<em>Focus is your greatest competitive advantage</em>
					<p class="author">- Focal Path</p>
				</div>

				<div class="content has-text-right">
					<!-- <em>Stop chasing 10 goals. Win at 3.</em>  -->
					<em>I am not afraid of the man who has practiced a thousand kicks. I am afraid of the man who has practiced one kick a thousand times</em>
					<p class="author">- Bruce lee</p>
				</div>

				<div class="extra-help-checkbox">
					<label class="checkbox">
						<input
							class="checkbox"
							type="checkbox"
							bind:checked={isExtraHelpNeeded}
							style="transform: scale(1.5);"
						/>
						Show me advanced prioritization insights
					</label>
				</div>

				{#if isExtraHelpNeeded}
					<div class="early-adopter-section">
						{#if waitlistSubmitSuccess}
							<div class="success-message">
								<div class="success-icon">✓</div>
								<h4>You're on the list!</h4>
								<p>We'll notify you when advanced features are ready.</p>
							</div>
						{:else}
							<div class="early-adopter-header">
								<h4>🚀 Advanced Features Coming Soon</h4>
								<p>
									We're building powerful new prioritization insights including the
									<strong>Definitive Focus Guide</strong> and <strong>Goal Dashboards</strong>, 
									.
								</p>
								<p class="early-adopter-bonus">
									🎁 <strong>Early adopters</strong> get exclusive discounts, bonuses and priority access!
								</p>
							</div>

							<form class="early-adopter-form" onsubmit={handleWaitlistSubmit}>
								<div class="form-row">
									<input 
										type="email" 
										bind:value={waitlistForm.email}
										placeholder="your@email.com"
										required
										disabled={isSubmittingWaitlist}
									/>
									<button 
										type="submit" 
										class="waitlist-submit-btn"
										disabled={isSubmittingWaitlist}
									>
										{isSubmittingWaitlist ? 'Joining...' : 'Notify Me'}
									</button>
								</div>
								<p class="privacy-note">We respect your privacy. No spam, ever.</p>
							</form>
						{/if}
					</div>
				{/if}
			</div>
		</footer>
	{/if}
</div>

<style>
	/* Prioritization-specific styles */
	.prioritization-hero {
		background: linear-gradient(135deg, #9cfffa 0%, #084b83 100%);
		color: white;
		padding: 1rem 0;
		min-height: 200px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.prioritization-title {
		font-size: 2.5rem !important;
		font-weight: 700 !important;
		color: white !important;
		margin-bottom: 0.5rem !important;
		line-height: 1.2;
		text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		font-family: "IBM Plex Mono", monospace !important;
	}

	.prioritization-subtitle {
		color: rgba(255, 255, 255, 0.9) !important;
		font-size: 1.1rem;
		font-weight: 500;
		margin-top: 0.5rem !important;
		opacity: 0.9;
	}

	/* Responsive typography */
	@media (min-width: 768px) {
		.prioritization-title {
			font-size: 3rem !important;
		}

		.prioritization-subtitle {
			font-size: 1.2rem;
		}
	}

	@media (min-width: 1024px) {
		.prioritization-title {
			font-size: 3.5rem !important;
		}

		.prioritization-subtitle {
			font-size: 1.3rem;
		}
	}

	/* Header container styling */
	.prioritization-hero .container {
		transition: all 0.3s ease;
		cursor: pointer;
		border-radius: 12px;
		padding: 1rem;
	}

	.prioritization-hero .container:hover {
		background: rgba(255, 255, 255, 0.1);
		transform: translateY(-2px);
	}

	/* Header Logo Styles */
	.header-logo-container {
		margin-bottom: 1rem;
	}

	.header-logo {
		height: 60px;
		width: auto;
		filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
	}

	@media (min-width: 768px) {
		.header-logo {
			height: 70px;
		}
	}

	@media (min-width: 1024px) {
		.header-logo {
			height: 80px;
		}
		
		.header-logo-container {
			margin-bottom: 1.5rem;
		}
	}

	/* Templates Section */
	.templates-container {
		max-width: 800px;
		margin: 2rem auto;
		padding: 2rem;
		text-align: center;
	}

	.templates-container h3 {
		font-size: 1.5rem;
		margin-bottom: 2rem;
		color: #333;
	}

	.templates-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
		gap: 1.5rem;
		margin-bottom: 2rem;
	}

	.template-card {
		background: white;
		border: 2px solid #e0e0e0;
		border-radius: 12px;
		padding: 1.5rem;
		cursor: pointer;
		transition: all 0.3s ease;
		text-align: left;
		width: 100%;
	}

	.template-card:hover {
		border-color: #9cfffa;
		box-shadow: 0 4px 15px rgba(156, 255, 250, 0.3);
		transform: translateY(-2px);
	}

	.template-card h4 {
		color: #084b83;
		margin-bottom: 0.5rem;
		font-size: 1.2rem;
	}

	.template-card p {
		color: #666;
		margin-bottom: 1rem;
		font-size: 0.9rem;
	}

	.template-criteria {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
	}

	.criterion-tag {
		background: #f0f8ff;
		color: #084b83;
		padding: 0.3rem 0.6rem;
		border-radius: 15px;
		font-size: 0.8rem;
		border: 1px solid #9cfffa;
	}

	.skip-templates {
		background: transparent;
		color: #666;
		border: 1px solid #ccc;
		padding: 0.8rem 1.5rem;
		border-radius: 8px;
		cursor: pointer;
		transition: all 0.3s ease;
	}

	.skip-templates:hover {
		background: #f5f5f5;
		border-color: #999;
	}

	/* Import Card Styles */
	.import-card {
		border-style: dashed;
		border-color: #ccc;
		background: #fafafa;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
	}

	.import-card:hover {
		border-color: #9cfffa;
		background: #f0ffff;
	}

	.import-card.dragging {
		border-color: #00c9b7;
		background: #e0fffc;
		box-shadow: 0 0 20px rgba(0, 201, 183, 0.3);
		transform: scale(1.02);
	}

	.import-icon {
		font-size: 2.5rem;
		margin-bottom: 0.5rem;
	}

	.import-card h4 {
		color: #666;
		margin-bottom: 0.3rem;
	}

	.import-card:hover h4 {
		color: #084b83;
	}

	.import-card p {
		color: #888;
		margin-bottom: 0.8rem;
	}

	.import-hint {
		font-size: 0.75rem;
		color: #999;
		padding: 0.4rem 0.8rem;
		background: #f0f0f0;
		border-radius: 4px;
	}

	.import-card.dragging .import-hint {
		background: #00c9b7;
		color: white;
	}

	.drop-hint {
		font-weight: 600;
	}

	/* Focus Matrix Styles */
	.focus-matrix {
		max-width: 800px;
		margin: 3rem auto;
		padding: 2rem;
	}

	.focus-matrix h2 {
		text-align: center;
		color: #084b83;
		margin-bottom: 2rem;
	}

	.matrix-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1rem;
	}

	.matrix-quadrant {
		background: white;
		border-radius: 10px;
		padding: 1.5rem;
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
		min-height: 150px;
	}

	.high-impact-low-effort {
		border-left: 4px solid #4caf50;
		background: linear-gradient(135deg, #e8f5e8, #f1f8e9);
	}

	.high-impact-high-effort {
		border-left: 4px solid #ff9800;
		background: linear-gradient(135deg, #fff3e0, #fef7ed);
	}

	.low-impact-low-effort {
		border-left: 4px solid #2196f3;
		background: linear-gradient(135deg, #e3f2fd, #f0f8ff);
	}

	.low-impact-high-effort {
		border-left: 4px solid #f44336;
		background: linear-gradient(135deg, #ffebee, #fef5f5);
	}

	.matrix-quadrant h4 {
		margin: 0 0 0.5rem 0;
		font-size: 1.1rem;
	}

	.matrix-quadrant p {
		margin: 0 0 1rem 0;
		font-size: 0.9rem;
		color: #666;
	}

	.matrix-item {
		background: rgba(255, 255, 255, 0.8);
		padding: 0.5rem;
		margin-bottom: 0.5rem;
		border-radius: 5px;
		font-size: 0.9rem;
		border: 1px solid rgba(0, 0, 0, 0.1);
	}

	/* Button Styles */
	.priority-button {
		background: linear-gradient(135deg, #9cfffa, #7de8e3);
		color: #084b83;
		border: none;
		padding: 1rem 2rem;
		font-size: 1.1rem;
		font-weight: 600;
		border-radius: 12px;
		cursor: pointer;
		transition: all 0.3s ease;
		box-shadow: 0 4px 15px rgba(156, 255, 250, 0.3);
	}

	.priority-button:hover {
		background: linear-gradient(135deg, #7de8e3, #5dd9d2);
		transform: translateY(-2px);
		box-shadow: 0 6px 20px rgba(156, 255, 250, 0.4);
	}

	/* Inherit base styles from original FocalPath */
	.fade-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background: rgba(0, 0, 0, 0.3);
		z-index: 999;
		pointer-events: none;
		animation: fade-in 0.3s ease-out;
	}

	.button-loading-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 20px 32px;
		background: white;
		border-radius: 12px;
		position: relative;
		z-index: 1000;
	}

	.spinning-icon-container {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.spinning-priority-icon {
		width: 48px;
		height: 48px;
		animation: spin 1.5s linear infinite;
		filter: brightness(0); /* Makes the white logo appear black */
	}

	.weighing-text {
		font-size: 0.9rem;
		color: #4a5568;
		font-weight: 500;
		font-style: italic;
		text-align: center;
	}

	@keyframes fade-in {
		0% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	.tips-container {
		display: flex;
		justify-content: center;
		gap: 0.5rem;
		margin: 1rem 0;
		width: 100%;
		max-width: 600px;
	}

	.appContent {
		display: flex;
		flex-direction: column;
		align-items: center;
		font-size: large;
	}

	/* Textarea container styling */
	.textarea-container {
		width: 90vw;
		max-width: 600px;
		margin: 1rem;
	}

	textarea {
		height: 200px;
		width: 100%;
		padding: 15px;
		border-radius: 10px;
		box-sizing: border-box;
		transition: border 0.3s ease;
		resize: vertical;
		font-size: large;
	}

	/* Mobile-specific adjustments */
	@media (max-width: 768px) {
		.textarea-container {
			width: 95vw;
		}

		textarea {
			font-size: 1rem;
		}
	}

	/* Criteria emoji styling */
	.criteria-emoji {
		user-select: none;
		line-height: 1;
	}

	#assessment-section {
		width: 100%;
		max-width: 600px;
		margin: 0 auto;
		padding: 1rem;
		box-sizing: border-box;
	}

	.criteria-container {
		width: 100% !important;
		max-width: 600px !important;
		margin: 1em auto !important;
		box-sizing: border-box;
	}

	#results-section {
		display: flex;
		justify-content: center;
		align-items: center;
		flex-direction: column;
	}

	.extra-help-checkbox {
		text-align: center;
		font-size: medium;
		margin: 1rem;
		padding: 1em;
	}

	.author {
		font-size: smaller;
		padding: 0.5em;
	}

	/* Responsive Design */
	@media (max-width: 768px) {
		.templates-grid {
			grid-template-columns: 1fr;
		}

		.matrix-grid {
			grid-template-columns: 1fr;
		}

		.focus-matrix {
			margin: 2rem 1rem;
			padding: 1rem;
		}
	}

	/* Early Adopter Section Styles */
	.early-adopter-section {
		margin-top: 1.5rem;
		padding: 1.5rem;
		background: linear-gradient(135deg, #f8f9fa 0%, #e9f7f6 100%);
		border-radius: 12px;
		border: 1px solid #9cfffa;
		text-align: center;
		max-width: 500px;
		margin-left: auto;
		margin-right: auto;
	}

	.early-adopter-header h4 {
		color: #084b83;
		font-size: 1.2rem;
		margin-bottom: 0.75rem;
	}

	.early-adopter-header p {
		color: #555;
		font-size: 0.95rem;
		line-height: 1.5;
		margin-bottom: 0.5rem;
	}

	.early-adopter-bonus {
		background: #fff3e0;
		padding: 0.5rem 1rem;
		border-radius: 8px;
		margin-top: 0.75rem;
		font-size: 0.9rem;
	}

	.early-adopter-form {
		margin-top: 1rem;
	}

	.form-row {
		display: flex;
		gap: 0.5rem;
		flex-wrap: wrap;
		justify-content: center;
	}

	.form-row input[type="email"] {
		flex: 1;
		min-width: 200px;
		padding: 0.75rem 1rem;
		border: 1px solid #ccc;
		border-radius: 8px;
		font-size: 1rem;
	}

	.form-row input[type="email"]:focus {
		outline: none;
		border-color: #9cfffa;
		box-shadow: 0 0 0 3px rgba(156, 255, 250, 0.3);
	}

	.waitlist-submit-btn {
		background: linear-gradient(135deg, #9cfffa 0%, #7de8e3 100%);
		color: #084b83;
		border: none;
		padding: 0.75rem 1.5rem;
		border-radius: 8px;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.3s ease;
	}

	.waitlist-submit-btn:hover:not(:disabled) {
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(156, 255, 250, 0.4);
	}

	.waitlist-submit-btn:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.privacy-note {
		font-size: 0.8rem;
		color: #888;
		margin-top: 0.75rem;
	}

	.success-message {
		padding: 1rem;
	}

	.success-icon {
		width: 50px;
		height: 50px;
		background: #4caf50;
		color: white;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.5rem;
		font-weight: bold;
		margin: 0 auto 1rem;
	}

	.success-message h4 {
		color: #084b83;
		margin-bottom: 0.5rem;
	}

	.success-message p {
		color: #555;
	}

	@media (max-width: 500px) {
		.form-row {
			flex-direction: column;
		}

		.form-row input[type="email"] {
			min-width: 100%;
		}

		.waitlist-submit-btn {
			width: 100%;
		}
	}

	/* Save Button Styles */
	.save-button-container {
		position: relative;
		display: inline-block;
	}

	.save-button {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		background: white;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		padding: 10px 15px;
		cursor: pointer;
		transition: all 0.2s ease;
		margin: 10px 0;
	}

	.save-button:hover {
		border-color: #b5b5b5;
		color: #363636;
	}

	.save-button:active {
		transform: scale(0.98);
	}

	.save-button svg {
		width: 20px;
		height: 20px;
		color: #4a4a4a;
	}

	.save-button .chevron-icon {
		width: 14px;
		height: 14px;
		transition: transform 0.2s ease;
	}

	.save-button .chevron-icon.rotated {
		transform: rotate(180deg);
	}

	/* Save Menu Dropdown */
	.save-menu {
		position: absolute;
		bottom: 100%;
		right: 0;
		margin-bottom: 8px;
		background: white;
		border: 1px solid #dbdbdb;
		border-radius: 8px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		overflow: hidden;
		min-width: 220px;
		z-index: 100;
		animation: slideUp 0.15s ease-out;
	}

	@keyframes slideUp {
		from {
			opacity: 0;
			transform: translateY(8px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.save-menu-item {
		display: flex;
		align-items: center;
		gap: 10px;
		width: 100%;
		padding: 12px 16px;
		background: none;
		border: none;
		text-align: left;
		font-size: 0.9rem;
		color: #363636;
		cursor: pointer;
		transition: background-color 0.15s ease;
	}

	.save-menu-item:hover {
		background-color: #f5f5f5;
	}

	.save-menu-item:not(:last-child) {
		border-bottom: 1px solid #f0f0f0;
	}

	.save-menu-item .menu-icon {
		font-size: 1.1rem;
	}
</style>
